import * as yup from 'yup';
import { Schema } from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Grid, Button, FormControlLabel, Checkbox, InputAdornment } from '@mui/material';
import React, { useState, useRef } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import ControlledTextField from 'src/shared/components/form/controlledTextField';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import LoadingOverlay from 'src/shared/components/organisms/loadingOverlay';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import ControlledSelectBox from 'src/shared/components/form/controlledSelectBox';
import { createDonorSubscription } from 'src/shared/services/donorSubscriptionService';
import ControlledDatePicker from 'src/shared/components/form/controlledDatePicker';
import { DateHelper } from 'src/shared/helpers/dateHelper';
import { CurrencyEnum } from 'src/shared/models/enums/currency.enum';
import { MainSettingsEnum } from 'src/shared/models/enums/mainSettings.enum';
import { useGlobals } from 'src/shared/contexts/appContext';
import { getMainSetting } from 'src/shared/functions/getMainSetting';
import { FormDonorSubscriptionPutItem, MonthIntervalType } from '../models/donor';
import { FundraiserPicker } from './fundraiserPicker';

type DonorSubscriptionsCreateModalProps = {
  modalOpen: boolean;
  donorId: number;
  onSave: () => void;
  onClose: () => void;
};
export const DonorSubscriptionsCreateModal = (props: DonorSubscriptionsCreateModalProps) => {
  const { modalOpen, onSave, onClose, donorId } = props;

  const { t } = useTranslation(['gift', 'common']);

  const { currentUser } = useGlobals();
  const currency = getMainSetting(currentUser?.mainSettings ?? [], MainSettingsEnum.Currency, CurrencyEnum.SEK);

  const [loading, setIsLoading] = useState(false);
  const [serverErrors, setServerErrors] = useState<string[]>([]);
  const [checked, setChecked] = React.useState(false);

  const handleCheckbox = (event: React.ChangeEvent<HTMLInputElement>) => {
    setChecked(event.target.checked);
  };

  // Make a reference to the top portion of the modal content
  // When an error occures, scroll top of modal into view
  const modalTop = useRef<HTMLDivElement>(null);
  const scrollToModalTop = () => modalTop.current && modalTop.current.scrollIntoView();

  const isMounted = useIsMounted();

  const DonorSubscriptionRequestPostSchema: Schema<FormDonorSubscriptionPutItem> = yup.object().shape({
    id: yup.number().nullable(),
    donor_id: yup.number().required(),
    fundraiser_id: yup.number().min(1, t('common:validationMessages.required')).required(t('common:validationMessages.required')),
    subscriptionStart: yup
      .date()
      .transform((v) => (v instanceof Date && !Number.isNaN(v) ? v : null))
      .required(t('common:validationMessages.required'))
      .typeError(t('common:validationMessages.invalidFormat')),
    subscriptionLength: yup.number().notRequired(),
    continuous: yup.boolean().required(),
    amount: yup.number().min(10, t('gift:createDonor.amountValidation')).typeError(t('common:validationMessages.invalidFormat')).required(),
    monthlyFrequency: yup.mixed<MonthIntervalType>().oneOf([1, 3, 12], t('gift:createDonor.frequencyValidation')).required(),
  });
  const formMethods = useForm({
    mode: 'onChange',
    resolver: yupResolver(DonorSubscriptionRequestPostSchema),
    defaultValues: {
      id: 0,
      donor_id: donorId,
      fundraiser_id: 0,
      subscriptionStart: null,
      subscriptionLength: 0,
      continuous: false,
      amount: 0,
      monthlyFrequency: 1,
    },
  });

  const { handleSubmit } = formMethods;

  const saveFormData = React.useCallback(
    async (data: FormDonorSubscriptionPutItem) => {
      const formData = {
        id: 0,
        donor_id: data.donor_id,
        fundraiser_id: data.fundraiser_id,
        subscriptionStart: DateHelper.postDate(data.subscriptionStart),
        subscriptionLength: data.subscriptionLength,
        continuous: checked,
        amount: data.amount,
        monthlyFrequency: data.monthlyFrequency,
      };
      ConsoleHelper.log('formData to put', formData);
      setIsLoading(true);
      try {
        const res = await createDonorSubscription(formData);
        if (isMounted()) {
          toast.success(<>{t('gift:editDonor.saveSuccess')}</>);
          setServerErrors([]);
          if (res.data) {
            setIsLoading(false);
            onSave();
          }
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (e: any) {
        if (isMounted()) {
          ConsoleHelper.log('saveFormDataError:', e);
          setIsLoading(false);
          setServerErrors(e.data);
          scrollToModalTop();
        }
      }
    },
    [checked, isMounted, onSave, t],
  );

  const onSubmit = React.useCallback(
    (data: FormDonorSubscriptionPutItem) => {
      saveFormData(data);
    },
    [saveFormData],
  );

  const onHandleClose = React.useCallback(() => {
    onClose();
  }, [onClose]);

  const onHandleSave = React.useCallback(() => {
    handleSubmit(onSubmit)();
  }, [handleSubmit, onSubmit]);

  return (
    <GenericDialog
      open={modalOpen}
      padding={0}
      dialogTitle={t('gift:createDonorSubscription')}
      onClose={onHandleClose}
      onSave={onHandleSave}
      dialogContent={
        <Box ref={modalTop} sx={{ py: 2, border: (theme) => (serverErrors.length > 0 ? `8px solid ${theme.palette.error.main}` : 'none') }}>
          <LoadingOverlay loading={loading} />
          <FormProvider {...formMethods}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Box sx={{ px: 2 }}>
                <GridWrapper>
                  <Grid item xs={12} sm={6} md={4}>
                    <ControlledTextField
                      name='amount'
                      label={t('gift:paymentDetail.amount')}
                      inputProps={{ maxLength: 5 }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      // eslint-disable-next-line react/jsx-no-duplicate-props
                      InputProps={{
                        endAdornment: <InputAdornment position='end'>{currency}</InputAdornment>,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <ControlledSelectBox
                      items={[
                        { label: t('gift:donorSubscriptions.monthly'), value: 1 },
                        { label: t('gift:donorSubscriptions.quarterly'), value: 3 },
                        { label: t('gift:donorSubscriptions.annually'), value: 12 },
                      ]}
                      name='monthlyFrequency'
                      label={t('gift:paymentDetail.paymentFrequency')}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FundraiserPicker initialValue={0} label={t('gift:paymentDetail.fundraiser')} name='fundraiser_id' />
                  </Grid>
                </GridWrapper>
              </Box>
              <Box sx={{ px: 2, mt: 2 }}>
                <GridWrapper>
                  <Grid item xs={12} sm={6} md={4}>
                    <ControlledDatePicker label={t('gift:paymentDetail.subscriptionStart')} name='subscriptionStart' />
                  </Grid>
                  <Grid item xs>
                    <ControlledTextField
                      name='subscriptionLength'
                      label={t('gift:paymentDetail.subscriptionLength')}
                      disabled={checked}
                      inputProps={{ maxLength: 2 }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  </Grid>
                  <Grid item xs>
                    <FormControlLabel
                      label={t('gift:paymentDetail.continuous')}
                      control={<Checkbox name='continuous' checked={checked} onChange={handleCheckbox} />}
                    />
                  </Grid>
                </GridWrapper>
              </Box>
            </form>
          </FormProvider>
        </Box>
      }
      dialogActions={
        <Box display='flex' justifyContent='end' sx={{ width: '100%' }}>
          <Box>
            <Button variant='outlined' color='primary' disableElevation onClick={onHandleClose} sx={{ mr: 1 }}>
              {t('common:button.cancel')}
            </Button>
            <Button autoFocus variant='contained' color='primary' disableElevation onClick={onHandleSave}>
              {t('common:button.save')}
            </Button>
          </Box>
        </Box>
      }
    />
  );
};
